import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material'
import {
  Archive,
  Warning,
  TrendingUp,
  Storage,
  Schedule,
  Assessment
} from '@mui/icons-material'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import { format, parseISO } from 'date-fns'
import archiveService from '../../../services/archiveService'

const ArchiveDashboard = ({ data, loading }) => {
  const [dashboardData, setDashboardData] = useState(null)
  const [localLoading, setLocalLoading] = useState(false)

  useEffect(() => {
    if (!data) {
      loadDashboardData()
    } else {
      setDashboardData(data)
    }
  }, [data])

  const loadDashboardData = async () => {
    setLocalLoading(true)
    try {
      const response = await archiveService.getArchiveDashboard()
      if (response.data.success) {
        setDashboardData(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLocalLoading(false)
    }
  }

  const isLoading = loading || localLoading

  if (isLoading) {
    return <LinearProgress />
  }

  if (!dashboardData) {
    return (
      <Alert severity="info">
        No dashboard data available
      </Alert>
    )
  }

  const { summary, recentActivities, archiveTypeDistribution, conditionOverview, dueForDestruction, spaceUtilization } = dashboardData

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

  // Prepare chart data
  const typeChartData = archiveTypeDistribution?.map(item => ({
    name: item._id,
    value: item.count
  })) || []

  const conditionChartData = conditionOverview?.map(item => ({
    name: item._id,
    value: item.count
  })) || []

  const spaceChartData = spaceUtilization?.slice(0, 10).map(item => ({
    rack: item.rack,
    files: item.fileCount,
    shelves: item.shelfCount
  })) || []

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Archive sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h3" color="primary" fontWeight={700}>
                {summary?.totalArchives || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Total Archives
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h3" color="success.main" fontWeight={700}>
                {summary?.recentArchives || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Recent Archives (30 days)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Warning sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h3" color="warning.main" fontWeight={700}>
                {summary?.pendingDestruction || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Pending Destruction
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Assessment sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h3" color="info.main" fontWeight={700}>
                {summary?.accessedThisMonth || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Accessed This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Archive Type Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Archive Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={typeChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {typeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Condition Overview */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Archive Condition Overview
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={conditionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Space Utilization */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Space Utilization by Rack (Top 10)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={spaceChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="rack" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="files" fill="#0088FE" name="Files" />
                  <Bar dataKey="shelves" fill="#00C49F" name="Shelves" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recent Activities
              </Typography>
              <List dense>
                {recentActivities?.length > 0 ? (
                  recentActivities.slice(0, 8).map((activity, index) => (
                    <React.Fragment key={activity._id || index}>
                      <ListItem>
                        <ListItemIcon>
                          <Archive fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {activity.mrdFile?.ipdNumber || activity.mrdFile?.fileNumber}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Accessed by {activity.accessedBy?.name} • {' '}
                              {activity.lastAccessed ? format(parseISO(activity.lastAccessed), 'MMM dd') : 'Recently'}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary">
                          No recent activities
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Files Due for Destruction */}
        {dueForDestruction?.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Files Due for Destruction This Month
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Archive Number</TableCell>
                        <TableCell>File Number</TableCell>
                        <TableCell>Patient Name</TableCell>
                        <TableCell>Destruction Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dueForDestruction.slice(0, 10).map((archive) => (
                        <TableRow key={archive._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {archive.archiveNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {archive.mrdFile?.ipdNumber || archive.mrdFile?.fileNumber}
                          </TableCell>
                          <TableCell>
                            {archive.mrdFile?.patientName}
                          </TableCell>
                          <TableCell>
                            {archive.destructionDate ? format(parseISO(archive.destructionDate), 'MMM dd, yyyy') : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Pending"
                              color="warning"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default ArchiveDashboard